import React, { useEffect, useState } from "react";
import { Siren, PhoneCall, MapPin, Building2, HeartPulse, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate, formatErr } from "../../lib/api";
import { Loading, PageHeader, StatusPill } from "../../components/Shared";

export default function Emergency() {
  const [cases, setCases] = useState(null);
  const [actionText, setActionText] = useState({});
  const load = () => api.get("/admin/emergencies").then((r) => setCases(r.data));
  useEffect(() => { load().catch(() => setCases([])); const t = setInterval(() => load().catch(() => {}), 20000); return () => clearInterval(t); }, []);
  const addAction = async (id) => {
    try {
      await api.post(`/admin/emergencies/${id}/actions`, { text: actionText[id] });
      setActionText({ ...actionText, [id]: "" }); toast.success("Action logged"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const resolve = async (id) => {
    try { await api.post(`/admin/emergencies/${id}/resolve`); toast.success("Case resolved"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  if (!cases) return <Loading />;
  const open = cases.filter((c) => c.status === "open");
  const closed = cases.filter((c) => c.status !== "open");
  const CaseCard = ({ c }) => (
    <div className={`rounded-3xl border-2 p-6 ${c.status === "open" ? "border-red-400 bg-white" : "border-stone-200 bg-white opacity-75"}`} data-testid={`emergency-case-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-heading text-xl font-bold text-stone-900">
            {c.status === "open" && <Siren className="h-6 w-6 animate-pulse text-red-600" />}
            {c.parent?.name || "Parent"}
          </p>
          <p className="mt-1 flex items-center gap-1 text-sm text-stone-500"><MapPin className="h-4 w-4" /> {c.parent?.address}</p>
          <p className="text-xs text-stone-400">Opened {fmtDate(c.opened_at)} · by {c.opened_by}</p>
        </div>
        <StatusPill status={c.status === "open" ? "emergency" : "resolved"} />
      </div>
      <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
        <div className="rounded-xl bg-stone-50 p-3">
          <p className="p1-label">Family & contacts</p>
          {(c.contacts || []).sort((a, b) => a.priority - b.priority).map((ct) => (
            <p key={ct.id} className="flex justify-between"><span>{ct.name} ({ct.relation})</span>{ct.phone ? <a href={`tel:${ct.phone.replace(/\s/g, "")}`} className="font-semibold text-emerald-900"><PhoneCall className="inline h-3.5 w-3.5" /> {ct.phone}</a> : <span className="text-xs text-stone-400">Number on file soon</span>}</p>
          ))}
          <p className="mt-1 text-xs text-stone-500">Care Manager: <strong>{c.care_manager?.name || "—"}</strong> {c.care_manager?.phone}</p>
        </div>
        <div className="rounded-xl bg-stone-50 p-3">
          <p className="p1-label"><Building2 className="inline h-3.5 w-3.5" /> Preferred hospital</p>
          <p className="font-semibold">{c.parent?.preferred_hospital || "—"}</p>
          {c.medical && <p className="mt-1 text-xs text-amber-800"><HeartPulse className="inline h-3.5 w-3.5" /> {(c.medical.conditions || []).join(", ")} · Allergies: {(c.medical.allergies || []).join(", ") || "none"}</p>}
          <p className="mt-2 text-xs text-red-700">Life-threatening? Call 112 / 108 first.</p>
        </div>
      </div>
      <div className="mt-4">
        <p className="p1-label">Action timeline</p>
        <ul className="space-y-1.5">
          {(c.actions || []).map((a, i) => (
            <li key={i} className="flex gap-3 text-sm"><span className="whitespace-nowrap text-xs text-stone-400">{fmtDate(a.at)}</span><span className="text-stone-700"><strong>{a.by}:</strong> {a.text}</span></li>
          ))}
        </ul>
      </div>
      {c.status === "open" && (
        <div className="mt-4 flex flex-wrap gap-2">
          <input className="p1-input flex-1" placeholder="Log an action (e.g. 'Ambulance dispatched via PinkCity')"
            value={actionText[c.id] || ""} onChange={(e) => setActionText({ ...actionText, [c.id]: e.target.value })} data-testid={`emergency-action-input-${c.id}`} />
          <button onClick={() => addAction(c.id)} className="rounded-full bg-stone-800 px-5 py-2.5 text-sm font-bold text-white" data-testid={`emergency-action-btn-${c.id}`}>Log Action</button>
          <button onClick={() => resolve(c.id)} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-900 px-5 py-2.5 text-sm font-bold text-white" data-testid={`emergency-resolve-${c.id}`}><CheckCircle2 className="h-4 w-4" /> Resolve</button>
        </div>
      )}
    </div>
  );
  return (
    <div data-testid="admin-emergency">
      <PageHeader title="Emergency Console" sub="Every action is timestamped and audited. This complements — never replaces — 112 / 108." />
      {open.length === 0 && <p className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm font-semibold text-emerald-900" data-testid="no-emergencies">No open emergency cases. All calm.</p>}
      <div className="mt-4 space-y-5">{open.map((c) => <CaseCard key={c.id} c={c} />)}</div>
      {closed.length > 0 && (<><h2 className="mb-3 mt-8 font-heading text-lg font-semibold text-stone-500">Resolved</h2><div className="space-y-5">{closed.map((c) => <CaseCard key={c.id} c={c} />)}</div></>)}
    </div>
  );
}
